import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, DollarSign, MessageCircle, Repeat, Heart, Settings,
  Plus, Eye, Edit, Trash2, Clock, CheckCircle, XCircle, TrendingUp,
  BarChart3, Loader2
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TradeDetailPanel from '../components/TradeDetailPanel';
import { api, useAuth } from '../App';
import { getCategoryLabel } from '../lib/categories';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const TABS = [
  { id: 'listings', label: 'My Listings', icon: Package },
  { id: 'offers', label: 'Offers', icon: DollarSign },
  { id: 'trades', label: 'Trades', icon: Repeat },
  { id: 'earnings', label: 'Earnings', icon: BarChart3 },
  { id: 'wanted', label: 'Wanted', icon: Heart },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [offers, setOffers] = useState({ received: [], sent: [] });
  const [trades, setTrades] = useState({ received: [], sent: [] });
  const [wanted, setWanted] = useState([]);
  const [earnings, setEarnings] = useState({ orders: [], summary: { total_gross: 0, total_fees: 0, total_payout: 0, total_transactions: 0, seller_commission_pct: 7 } });
  const [earningsFilter, setEarningsFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [offerView, setOfferView] = useState('received');
  const [tradeView, setTradeView] = useState('received');
  const [selectedTradeId, setSelectedTradeId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all tab data to show accurate counts
      const [listingsRes, receivedOffersRes, sentOffersRes, receivedTradesRes, sentTradesRes, wantedRes, earningsRes] = await Promise.all([
        api.get('/my-listings'),
        api.get('/my-offers?type=received'),
        api.get('/my-offers?type=sent'),
        api.get('/my-trades?type=received'),
        api.get('/my-trades?type=sent'),
        api.get('/my-wanted'),
        api.get(`/my-earnings?status=${earningsFilter}`)
      ]);
      setListings(listingsRes.data);
      setOffers({ received: receivedOffersRes.data, sent: sentOffersRes.data });
      setTrades({ received: receivedTradesRes.data, sent: sentTradesRes.data });
      setWanted(wantedRes.data);
      setEarnings(earningsRes.data);
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [earningsFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteListing = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success('Listing deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete listing');
    }
  };

  const handleRespondToOffer = async (offerId, status) => {
    try {
      await api.put(`/offers/${offerId}?status=${status}`);
      toast.success(`Offer ${status}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update offer');
    }
  };

  const handleDeleteWanted = async (id) => {
    if (!window.confirm('Delete this wanted listing?')) return;
    try {
      await api.delete(`/wanted/${id}`);
      toast.success('Wanted listing deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white">
              Dashboard
            </h1>
            <p className="text-zinc-400">Welcome back, {user?.name}</p>
          </div>
          <Link
            to="/create-listing"
            className="btn-gold px-6 py-3 flex items-center justify-center gap-2"
            data-testid="create-listing-btn"
          >
            <Plus className="w-5 h-5" /> New Listing
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-6 bg-surface-card border border-border-subtle">
            <Package className="w-6 h-6 text-gold mb-2" />
            <p className="text-2xl font-clash font-semibold text-white">{listings.length}</p>
            <p className="text-sm text-zinc-400">Active Listings</p>
          </div>
          <div className="p-6 bg-surface-card border border-border-subtle">
            <DollarSign className="w-6 h-6 text-gold mb-2" />
            <p className="text-2xl font-clash font-semibold text-white">{offers.received.length}</p>
            <p className="text-sm text-zinc-400">Pending Offers</p>
          </div>
          <div className="p-6 bg-surface-card border border-border-subtle">
            <Repeat className="w-6 h-6 text-gold mb-2" />
            <p className="text-2xl font-clash font-semibold text-white">{trades.received.length}</p>
            <p className="text-sm text-zinc-400">Trade Proposals</p>
          </div>
          <div className="p-6 bg-surface-card border border-border-subtle">
            <TrendingUp className="w-6 h-6 text-gold mb-2" />
            <p className="text-2xl font-clash font-semibold text-white">
              {listings.filter(l => l.is_sold).length}
            </p>
            <p className="text-sm text-zinc-400">Items Sold</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-surface-card border border-border-subtle p-1 w-full justify-start">
            {TABS.map(tab => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex items-center gap-2 data-[state=active]:bg-gold data-[state=active]:text-black"
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings" className="space-y-4">
            {loading ? (
              <div className="space-y-4">
                {[1,2,3].map(i => (
                  <div key={i} className="h-24 bg-surface-card animate-pulse" />
                ))}
              </div>
            ) : listings.length > 0 ? (
              listings.map(listing => (
                <div key={listing.id} className="p-4 bg-surface-card border border-border-subtle flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-24 h-24 bg-surface-elevated flex-shrink-0">
                    {listing.images?.[0] ? (
                      <img 
                        src={listing.images[0].startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}`
                          : listing.images[0]}
                        alt="" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-zinc-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Link to={`/listing/${listing.id}`} className="font-medium text-white hover:text-gold">
                          {listing.title}
                        </Link>
                        <p className="text-sm text-zinc-400 mt-1">
                          ${listing.price?.toLocaleString()} · {listing.category.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {listing.is_sold ? (
                          <span className="badge-buy-now px-2 py-1 text-xs">Sold</span>
                        ) : listing.is_active ? (
                          <span className="badge-trade px-2 py-1 text-xs">Active</span>
                        ) : (
                          <span className="badge-auction px-2 py-1 text-xs">Inactive</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-sm text-zinc-400">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" /> {listing.views || 0} views
                      </span>
                    </div>
                  </div>
                  <div className="flex sm:flex-col gap-2">
                    <Link
                      to={`/listing/${listing.id}`}
                      className="btn-secondary px-3 py-2 text-sm flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => handleDeleteListing(listing.id)}
                      className="btn-secondary px-3 py-2 text-sm flex items-center gap-1 hover:border-red-500 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-16">
                <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">No listings yet</p>
                <Link to="/create-listing" className="btn-gold px-6 py-3 inline-block">
                  Create Your First Listing
                </Link>
              </div>
            )}
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers" className="space-y-4">
            <div className="flex gap-4 mb-4">
              <button
                onClick={() => setOfferView('received')}
                className={`px-4 py-2 text-sm ${offerView === 'received' ? 'bg-gold text-black' : 'bg-surface-card text-zinc-400'}`}
              >
                Received ({offers.received.filter(o => o.status === 'pending').length})
              </button>
              <button
                onClick={() => setOfferView('sent')}
                className={`px-4 py-2 text-sm ${offerView === 'sent' ? 'bg-gold text-black' : 'bg-surface-card text-zinc-400'}`}
              >
                Sent ({offers.sent.length})
              </button>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1,2].map(i => <div key={i} className="h-24 bg-surface-card animate-pulse" />)}
              </div>
            ) : (offerView === 'received' ? offers.received : offers.sent).length > 0 ? (
              (offerView === 'received' ? offers.received : offers.sent).map(offer => (
                <div key={offer.id} className="p-4 bg-surface-card border border-border-subtle">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-white">
                        ${offer.amount?.toLocaleString()} offer on {offer.listing_title}
                      </p>
                      <p className="text-sm text-zinc-400 mt-1">
                        {offerView === 'received' ? `From: ${offer.buyer_name}` : `Status: ${offer.status}`}
                      </p>
                      {offer.message && (
                        <p className="text-sm text-zinc-500 mt-2 italic">"{offer.message}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {offer.status === 'pending' ? (
                        <Clock className="w-5 h-5 text-yellow-500" />
                      ) : offer.status === 'accepted' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                  </div>
                  {offerView === 'received' && offer.status === 'pending' && (
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => handleRespondToOffer(offer.id, 'accepted')}
                        className="btn-gold px-4 py-2 text-sm"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleRespondToOffer(offer.id, 'rejected')}
                        className="btn-secondary px-4 py-2 text-sm"
                      >
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-16">
                <DollarSign className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No offers yet</p>
              </div>
            )}
          </TabsContent>

          {/* Trades Tab */}
          <TabsContent value="trades" className="space-y-4">
            {selectedTradeId ? (
              <TradeDetailPanel
                tradeId={selectedTradeId}
                onBack={() => { setSelectedTradeId(null); fetchData(); }}
              />
            ) : (
              <>
                <div className="flex gap-4 mb-4">
                  <button
                    onClick={() => setTradeView('received')}
                    className={`px-4 py-2 text-sm ${tradeView === 'received' ? 'bg-gold text-black' : 'bg-surface-card text-zinc-400'}`}
                  >
                    Received ({trades.received.length})
                  </button>
                  <button
                    onClick={() => setTradeView('sent')}
                    className={`px-4 py-2 text-sm ${tradeView === 'sent' ? 'bg-gold text-black' : 'bg-surface-card text-zinc-400'}`}
                  >
                    Sent ({trades.sent.length})
                  </button>
                </div>

                {loading ? (
                  <div className="space-y-4">
                    {[1,2].map(i => <div key={i} className="h-32 bg-surface-card animate-pulse" />)}
                  </div>
                ) : (tradeView === 'received' ? trades.received : trades.sent).length > 0 ? (
                  (tradeView === 'received' ? trades.received : trades.sent).map(trade => {
                    const statusColors = {
                      pending: 'text-yellow-500', countered: 'text-blue-400', accepted: 'text-emerald-400',
                      confirmed: 'text-emerald-500', rejected: 'text-red-500', expired: 'text-zinc-500', cancelled: 'text-zinc-500'
                    };
                    return (
                      <button
                        key={trade.id}
                        onClick={() => setSelectedTradeId(trade.id)}
                        className="w-full p-4 bg-surface-card border border-border-subtle hover:border-gold/30 transition-colors text-left"
                        data-testid={`trade-item-${trade.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">
                              Trade: {trade.listing_title || trade.target_listing?.title || 'Unknown'}
                            </p>
                            <p className="text-sm text-zinc-400 mt-1">
                              {tradeView === 'received' ? `From: ${trade.proposer_name || trade.proposer?.name}` : `To: ${trade.receiver_name || trade.receiver?.name}`}
                            </p>
                            {trade.offered_listings?.length > 0 && (
                              <p className="text-xs text-zinc-500 mt-1">
                                {trade.offered_listings.length} item(s) offered
                                {trade.cash_amount > 0 && ` + $${trade.cash_amount.toLocaleString()}`}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className={`text-xs font-medium uppercase ${statusColors[trade.status] || 'text-zinc-400'}`}>
                              {trade.status}
                            </span>
                            {['pending', 'countered'].includes(trade.status) && (
                              <span className="text-xs text-zinc-600">
                                {new Date(trade.updated_at || trade.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-16">
                    <Repeat className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400">No trades yet</p>
                    <Link to="/marketplace?trade=true" className="text-gold text-sm hover:underline mt-2 inline-block">
                      Browse trade-enabled items
                    </Link>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings" className="space-y-6" data-testid="earnings-tab">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 bg-surface-card border border-border-subtle">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Gross Sales</p>
                <p className="text-2xl font-clash font-bold text-white">${earnings.summary.total_gross.toLocaleString()}</p>
              </div>
              <div className="p-5 bg-surface-card border border-border-subtle">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Platform Fees ({earnings.summary.seller_commission_pct}%)</p>
                <p className="text-2xl font-clash font-bold text-red-400">-${earnings.summary.total_fees.toLocaleString()}</p>
              </div>
              <div className="p-5 bg-surface-card border border-gold/20">
                <p className="text-xs text-gold uppercase tracking-wider mb-2">Net Earnings</p>
                <p className="text-2xl font-clash font-bold text-gold">${earnings.summary.total_payout.toLocaleString()}</p>
              </div>
              <div className="p-5 bg-surface-card border border-border-subtle">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Transactions</p>
                <p className="text-2xl font-clash font-bold text-white">{earnings.summary.total_transactions}</p>
              </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-400">Filter:</span>
              {['all', 'paid', 'pending'].map(f => (
                <button
                  key={f}
                  onClick={() => { setEarningsFilter(f); }}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                    earningsFilter === f ? 'bg-gold text-black border-gold' : 'bg-surface-card text-zinc-400 border-border-subtle'
                  }`}
                  data-testid={`earnings-filter-${f}`}
                >
                  {f === 'all' ? 'All' : f}
                </button>
              ))}
            </div>

            {/* Order list */}
            {earnings.orders.length > 0 ? (
              <div className="space-y-3">
                {earnings.orders.map(order => (
                  <div key={order.id} className="p-4 bg-surface-card border border-border-subtle" data-testid={`earnings-order-${order.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{order.listing_title}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          Buyer: {order.buyer_name} &middot; {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm text-zinc-300">
                          Sale: <span className="text-white font-medium">${order.item_price?.toLocaleString() || order.gross_amount?.toLocaleString()}</span>
                        </p>
                        <p className="text-xs text-red-400 mt-0.5">
                          Fee: -${order.seller_fee?.toLocaleString() || '0'}
                        </p>
                        <p className="text-sm text-gold font-semibold mt-0.5">
                          Net: ${order.seller_payout?.toLocaleString() || order.gross_amount?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-2">No sales yet</p>
                <p className="text-sm text-zinc-500">When your listings sell, earnings will appear here.</p>
                <Link to="/create-listing" className="btn-gold px-6 py-3 inline-block mt-4">
                  Create a Listing
                </Link>
              </div>
            )}
          </TabsContent>

          {/* Wanted Tab */}
          <TabsContent value="wanted" className="space-y-4">
            <div className="flex justify-end mb-4">
              <Link to="/wanted" className="btn-gold px-4 py-2 text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Create Wanted
              </Link>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1,2].map(i => <div key={i} className="h-24 bg-surface-card animate-pulse" />)}
              </div>
            ) : wanted.length > 0 ? (
              wanted.map(item => (
                <div key={item.id} className="p-4 bg-surface-card border border-border-subtle flex justify-between items-start">
                  <div>
                    <p className="font-medium text-white">{item.title}</p>
                    <p className="text-sm text-zinc-400 mt-1">
                      {getCategoryLabel(item.category)}
                      {item.max_budget && ` · Budget: $${item.max_budget?.toLocaleString()}`}
                    </p>
                    <p className="text-sm text-zinc-500 mt-2">{item.description}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteWanted(item.id)}
                    className="btn-secondary px-3 py-2 text-sm hover:border-red-500 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-16">
                <Heart className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 mb-4">No wanted listings</p>
                <Link to="/wanted" className="btn-gold px-6 py-3 inline-block">
                  Create Wanted Listing
                </Link>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
}
